import { isRecord } from "./lib";

export interface ProofNode {
  label: string;
  ok: boolean | null;
  detail?: string;
  children: ProofNode[];
  pins?: Record<string, string>;
}

function node(
  label: string,
  ok: boolean | null,
  detail: string,
  children: ProofNode[] = [],
): ProofNode {
  return { label, ok, detail, children };
}

function asString(v: unknown): string {
  return typeof v === "string" ? v : "";
}

export function buildProofTreeFromReceipt(receipt: unknown): ProofNode | null {
  if (!isRecord(receipt)) return null;

  const kind = asString(receipt.kind);
  const reason = asString(receipt.reason);
  const pinsRaw = isRecord(receipt.pins) ? receipt.pins : null;

  const pins: Record<string, string> | undefined = pinsRaw
    ? Object.fromEntries(
        Object.entries(pinsRaw).map(([k, v]) => [
          k,
          typeof v === "string" ? v.slice(0, 12) + "…" : asString(v),
        ]),
      )
    : undefined;

  const fs = asString(receipt.forbidden_shape_id);
  const mb = asString(receipt.missing_bridge_id);

  const decisionOk = kind === "EXECUTE";
  const root: ProofNode = node(
    "Decision",
    decisionOk,
    `${kind} ${reason}`.trim(),
    [],
  );
  if (pins) root.pins = pins;

  // Gate nodes (conservative)
  const schemaOk = reason !== "SCHEMA_INVALID";
  const regimeOk = reason !== "BRIDGE_NOT_ALLOWED_BY_REGIME";
  const allowOk = reason !== "DOMAIN_NOT_ALLOWLISTED";
  const provOk = reason !== "UNTRUSTED_PROVENANCE_NO_BRIDGE";
  const witOk = reason !== "MISSING_WITNESSES";
  const matOk = reason !== "MATRIX_FORBIDS_SEQUENCE";
  const budOk = reason !== "BUDGET_FAILURE";

  root.children = [
    node("Schema valid", schemaOk, "SYSCALL-IR schema check"),
    node("Regime permits bridge", regimeOk, "Policy allowlist/blocklist check"),
    node("Allowlist permits domain", allowOk, "HTTP domain allowlist"),
    node(
      "Provenance allows execution",
      provOk,
      `Untrusted text cannot directly invoke execution without admitted bridge${fs ? " | forbidden_shape_id=" + fs : ""}${mb ? " | missing_bridge_id=" + mb : ""}`,
    ),
    node("Witnesses satisfied", witOk, "Required witnesses present"),
    node("Matrix permits sequence", matOk, "Bridge composition constraints"),
    node("Budget permits spend", budOk, "Monotone budget ledger + spend"),
  ];

  return root;
}

/**
 * Canonical demo proof tree for the dashboard's ProofTreePanel — the
 * untrusted-provenance refusal whose receipt cites the missing
 * `G1.EPISTEMIC_TO_EXEC_TEXT2HTTP` bridge. Derived from a synthetic
 * receipt via `buildProofTreeFromReceipt` so there is no duplicated
 * literal to drift from the builder.
 */
export const DEMO_PROOF_TREE: ProofNode = ((): ProofNode => {
  const tree = buildProofTreeFromReceipt({
    kind: "REFUSE",
    reason: "UNTRUSTED_PROVENANCE_NO_BRIDGE",
    forbidden_shape_id: "SEPARATE.MIXED_SQUARE.EPISTEMIC_TO_EXEC",
    missing_bridge_id: "G1.EPISTEMIC_TO_EXEC_TEXT2HTTP",
    pins: {
      schema_hash: "—",
      matrix_hash: "—",
      policy_hash: "—",
    },
  });
  if (!tree) throw new Error("DEMO_PROOF_TREE: builder returned null");
  return tree;
})();
